/** Mirrors sidebar visibility in App.tsx. A loaded access session overrides this seed. */

import { defaultTabForRole } from './safeUser';
import { normalizeAccessRole, sessionAllowsAction, sessionAllowsPage } from './accessSession';

const EXPENSE_APPROVE = ['ADMIN', 'CFO', 'CEO', 'CTO'];
const PAYROLL_RUN = ['ADMIN', 'CEO', 'CFO', 'CTO', 'HR'];
const LEAVE_APPROVE = ['ADMIN', 'MANAGER', 'CEO', 'CTO', 'HR', 'CFO', 'CCO', 'CBO', 'CGO', 'CHIEF_OF_OPERATIONS'];
const VOUCHER_SYNC = [
  'ADMIN',
  'CFO',
  'CEO',
  'CTO',
  'CCO',
  'CHIEF_OF_OPERATIONS',
  'CBO',
  'CGO',
  'MANAGER',
  'LEAD_OPERATOR',
  'USER',
];
const STOCK_DELETE = ['ADMIN', 'CEO'];
const SALES_IMPORT = ['ADMIN', 'CEO', 'CFO', 'CCO', 'CHIEF_OF_OPERATIONS'];

function listed(list: string[], role: string): boolean {
  return list.indexOf(role) !== -1;
}

/** Seed used until GET /api/access/me returns. Keep aligned with server seedAction. */
export function fallbackAction(role: string | undefined, action: string): boolean {
  const code = normalizeAccessRole(role);
  if (!code) return false;
  switch (action) {
    case 'expense.approve':
      return listed(EXPENSE_APPROVE, code);
    case 'payroll.run':
      return listed(PAYROLL_RUN, code);
    case 'leave.approve':
      return listed(LEAVE_APPROVE, code);
    case 'voucher.sync':
      return listed(VOUCHER_SYNC, code);
    case 'stock.delete':
      return listed(STOCK_DELETE, code);
    case 'sales.import':
      return listed(SALES_IMPORT, code);
    default:
      return false;
  }
}

export function canPerform(role: string | undefined, action: string): boolean {
  const live = sessionAllowsAction(role, action);
  if (live !== null) return live;
  return fallbackAction(role, action);
}

/** Team leave dashboard (earned / approved / remaining). Admin, CEO, HR only. */
export function canViewLeaveDashboard(role: string | undefined): boolean {
  if (!role) return false;
  return ['ADMIN', 'ADMINISTRATOR', 'CEO', 'HR'].includes(role);
}

/** Full payroll management (generate, approve, reissue, settings). */
export function canAccessPayroll(role: string | undefined): boolean {
  if (!role) return false;
  return ['ADMIN', 'ADMINISTRATOR', 'CFO', 'CEO', 'CTO', 'HR'].includes(role);
}

/** Master Audit Ledger → Salaries tab (approved payroll OPEX lines). */
export function canAccessSalaryAudit(role: string | undefined): boolean {
  if (!role) return false;
  return (
    canAccessPayroll(role) ||
    ['CCO', 'CHIEF_OF_OPERATIONS', 'CBO', 'CGO'].includes(role)
  );
}

/** HR directory and the Leave item under that menu. */
export function canAccessHr(role: string | undefined): boolean {
  if (!role) return false;
  return ['ADMIN', 'ADMINISTRATOR', 'CEO', 'CTO', 'HR', 'CFO'].includes(role);
}

export function canAccessDashboard(role: string | undefined): boolean {
  if (!role) return false;
  return !['ADMINISTRATIVE', 'USER'].includes(role);
}

export function canAccessOrders(role: string | undefined): boolean {
  if (!role) return false;
  if (['ADMINISTRATIVE', 'USER'].includes(role)) return false;
  if (role === 'LEAD_OPERATOR') return false;
  return canAccessDashboard(role);
}

export function canAccessVouchers(role: string | undefined): boolean {
  if (!role) return false;
  return [
    'ADMIN',
    'ADMINISTRATOR',
    'CFO',
    'CEO',
    'CTO',
    'CCO',
    'CHIEF_OF_OPERATIONS',
    'CBO',
    'CGO',
    'MANAGER',
    'LEAD_OPERATOR',
    'USER',
  ].includes(role);
}

export function canAccessProjects(role: string | undefined): boolean {
  if (!role) return false;
  if (role === 'ADMINISTRATIVE') return false;
  return true;
}

export function canViewProjectFinance(role: string | undefined): boolean {
  if (!role) return false;
  return ['ADMIN', 'ADMINISTRATOR', 'CFO', 'CEO', 'CTO', 'CCO', 'CHIEF_OF_OPERATIONS', 'CBO', 'CGO'].includes(role);
}

export function canMutateCampaigns(role: string | undefined): boolean {
  if (!role) return false;
  return canViewProjectFinance(role) || ['HR', 'MANAGER', 'LEAD_OPERATOR', 'USER'].includes(role);
}

export function canMutateCostCenters(role: string | undefined): boolean {
  if (!role) return false;
  return canViewProjectFinance(role) || role === 'MANAGER';
}

export function canAccessTab(role: string | undefined, tab: string): boolean {
  if (!role) return false;
  if (tab === 'settings') return true;
  const live = sessionAllowsPage(role, tab);
  if (live !== null) return live;
  const code = normalizeAccessRole(role);
  switch (tab) {
    case 'dashboard':
    case 'analysis':
    case 'inventory':
    case 'production':
    case 'qc':
      return canAccessDashboard(code);
    case 'orders':
    case 'customers':
      return canAccessOrders(code);
    case 'hr':
    case 'leave':
      return canAccessHr(code);
    case 'expenses':
    case 'financeAnalysis':
      return ['ADMIN', 'CFO', 'CEO', 'CTO', 'CCO', 'CHIEF_OF_OPERATIONS', 'CBO', 'CGO'].includes(code);
    case 'vouchers':
      return canAccessVouchers(code);
    case 'projects':
      return canAccessProjects(code);
    case 'payroll':
      return canAccessPayroll(code);
    case 'attendance':
      return code === 'ADMIN' || code === 'MANAGER';
    default:
      return canAccessDashboard(code);
  }
}

const OPEN_TAB_ORDER = [
  'dashboard',
  'analysis',
  'inventory',
  'vouchers',
  'projects',
  'production',
  'qc',
  'orders',
  'customers',
  'hr',
  'leave',
  'expenses',
  'payroll',
  'attendance',
  'financeAnalysis',
  'settings',
];

export function firstAccessibleTab(role: string | undefined, tabs: string[]): string | null {
  for (let i = 0; i < tabs.length; i++) {
    if (canAccessTab(role, tabs[i])) return tabs[i];
  }
  return null;
}

/** First page the role can open. Settings is always available to a signed-in role. */
export function firstOpenTab(role: string | undefined): string {
  const preferred = defaultTabForRole(role);
  if (canAccessTab(role, preferred)) return preferred;
  for (let i = 0; i < OPEN_TAB_ORDER.length; i++) {
    if (canAccessTab(role, OPEN_TAB_ORDER[i])) return OPEN_TAB_ORDER[i];
  }
  return 'settings';
}